<script setup lang="ts">
import { ref, watch } from 'vue';
import { ArrowPathIcon } from '@heroicons/vue/24/outline';
import CopyButton from '@/components/aml-review/CopyButton.vue';
import { useImmersveApiClient } from '@/hooks/useImmersveApiClient';
import type { CardToken } from '@/integrations/Immersve/card-tokens/types';
import { formatUtcTimestamp } from '@/util/datetime';
import { STATUS_TONE_CLASSES, requestorLabel, statusLabel } from './cardTokenDisplay';

// The endpoint's maximum: a card rarely has more, so one page is the usual case.
const PAGE_SIZE = 100;

const props = defineProps<{ cardId: string }>();

const api = useImmersveApiClient();
const tokens = ref<CardToken[]>([]);
const nextCursor = ref<string | undefined>();
const loading = ref(true);
const loadingMore = ref(false);
const error = ref<Error | null>(null);

function toError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err));
}

async function load() {
  loading.value = true;
  error.value = null;
  tokens.value = [];
  nextCursor.value = undefined;
  try {
    const page = await api.listCardTokens(props.cardId, { limit: PAGE_SIZE });
    tokens.value = page.items;
    nextCursor.value = page.pageInfo?.nextCursor;
  } catch (err) {
    error.value = toError(err);
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  loadingMore.value = true;
  try {
    const page = await api.listCardTokens(props.cardId, {
      limit: PAGE_SIZE,
      cursor: nextCursor.value,
    });
    tokens.value = [...tokens.value, ...page.items];
    nextCursor.value = page.pageInfo?.nextCursor;
  } catch (err) {
    error.value = toError(err);
  } finally {
    loadingMore.value = false;
  }
}

watch(() => props.cardId, load, { immediate: true });
</script>

<template>
  <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div
      class="hidden grid-cols-[1.4fr_0.8fr_1.1fr_1.2fr_1.6fr] bg-gray-50 text-xs font-semibold tracking-wide text-gray-500 uppercase md:grid"
    >
      <div class="px-4 py-2">Requestor</div>
      <div class="px-4 py-2">Token</div>
      <div class="px-4 py-2">Status</div>
      <div class="px-4 py-2">Requested</div>
      <div class="px-4 py-2">Token ID</div>
    </div>
    <div
      v-if="loading"
      data-test="card-tokens-loading"
      class="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-500"
    >
      <ArrowPathIcon class="h-4 w-4 animate-spin" />
      Loading…
    </div>
    <p v-else-if="error" data-test="card-tokens-error" class="px-4 py-6 text-sm text-red-600">
      {{ error.message }}
    </p>
    <p
      v-else-if="tokens.length === 0"
      data-test="card-tokens-empty"
      class="px-4 py-14 text-center text-sm text-gray-500"
    >
      This card has no tokens.
    </p>
    <template v-else>
      <ul role="list">
        <li
          v-for="token in tokens"
          :key="token.id"
          data-test="card-token-row"
          class="grid grid-cols-2 gap-x-4 gap-y-2 border-b border-gray-100 px-4 py-3 last:border-b-0 md:grid-cols-[1.4fr_0.8fr_1.1fr_1.2fr_1.6fr] md:gap-0 md:px-0"
        >
          <div class="col-span-2 flex min-w-0 flex-col justify-center md:col-span-1 md:px-4">
            <span class="text-sm font-medium text-gray-900" data-test="card-token-requestor">
              {{ requestorLabel(token) }}
            </span>
            <span
              v-if="token.description"
              class="truncate text-xs text-gray-500"
              data-test="card-token-description"
            >
              {{ token.description }}
            </span>
          </div>
          <div class="flex items-center md:px-4">
            <span class="font-mono text-sm text-gray-900" data-test="card-token-suffix">
              {{ token.tokenSuffix ? `•••• ${token.tokenSuffix}` : '—' }}
            </span>
          </div>
          <div class="flex items-center md:px-4">
            <span
              class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold capitalize ring-1 ring-inset"
              :class="STATUS_TONE_CLASSES[token.status]"
              data-test="card-token-status"
            >
              {{ statusLabel(token) }}
            </span>
          </div>
          <div class="flex items-center text-sm text-gray-700 md:px-4">
            {{ formatUtcTimestamp(token.provisioningRequestedAt) }}
          </div>
          <div class="col-span-2 flex min-w-0 items-center gap-1.5 md:col-span-1 md:px-4">
            <span class="truncate font-mono text-xs text-gray-500">{{ token.id }}</span>
            <CopyButton :value="token.id" label="token ID" />
          </div>
        </li>
      </ul>
      <div v-if="nextCursor" class="border-t border-gray-100 px-4 py-3 text-center">
        <button
          type="button"
          data-test="card-tokens-load-more"
          class="text-sm font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
          :disabled="loadingMore"
          @click="loadMore"
        >
          {{ loadingMore ? 'Loading…' : 'Load more' }}
        </button>
      </div>
    </template>
  </div>
</template>
